import React, {ComponentType, createContext, useContext, useRef} from 'react';
import {useControllerProps} from '../../../../Widget/ControllerContext';
import {FormInstance} from '../../../../Form/useFormInstance';

export type FormRegistration = {
  formInstance: FormInstance;
  focusOnForm: VoidFunction;
};

export type AdditionalDetailsDataContextType = {
  registerForm: (id: string, registration: FormRegistration) => void;
  unregisterForm: (id: string) => void;
  validateAllForms: () => Promise<boolean>;
  submitAndContinueToNextForm: (params: {currentForId: string}) => void;
  continueToNextStep: () => void;
};

export const AdditionalDetailsDataContext = createContext({} as AdditionalDetailsDataContextType);

export function withAdditionalDetailsData<T extends object>(Component: ComponentType<T>) {
  return function Wrapper(props: T) {
    const formsDataByIdRef = useRef<Record<string, FormRegistration>>({});

    const {
      formsStore: {submitForms},
      checkoutStore: {cart},
    } = useControllerProps();

    const registerForm = (id: string, registration: FormRegistration) => {
      formsDataByIdRef.current[id] = registration;
    };

    const unregisterForm = (id: string) => {
      const {[id]: _, ...rest} = formsDataByIdRef.current;
      formsDataByIdRef.current = rest;
    };

    const validateAllForms = async () => {
      const orderedFormIds = cart.forms.map((form) => form.id);

      const validationResults = await Promise.all(
        orderedFormIds.map(async (id) => {
          const formData = formsDataByIdRef.current[id];
          const isValid = formData?.formInstance.formRef.current
            ? await formData.formInstance.formRef.current.validate()
            : /* istanbul ignore next */ false;
          return {id, isValid};
        })
      );

      const firstInvalid = validationResults.find((result) => !result.isValid);

      if (firstInvalid) {
        const formData = formsDataByIdRef.current[firstInvalid.id];
        formData.focusOnForm();

        return false;
      }

      return true;
    };

    const submitAndContinueToNextForm = ({currentForId}: {currentForId: string}) => {
      const currentFormIndex = cart.forms.findIndex((form) => form.id === currentForId);
      const nextFormId = cart.forms[currentFormIndex + 1]?.id;

      const formData = formsDataByIdRef.current[nextFormId];
      formData?.focusOnForm();
    };

    const continueToNextStep = () => {
      const orderedFormIds = cart.forms.map((form) => {
        const {formInstance} = formsDataByIdRef.current[form.id];
        return {
          id: form.id,
          formSchemaId: form.formSchemaId,
          formValues: formInstance.dataRef.current.formValues,
        };
      });

      void submitForms(orderedFormIds);
    };

    return (
      <AdditionalDetailsDataContext.Provider
        value={{
          registerForm,
          unregisterForm,
          validateAllForms,
          continueToNextStep,
          submitAndContinueToNextForm,
        }}>
        <Component {...props} />
      </AdditionalDetailsDataContext.Provider>
    );
  };
}

export function useAdditionalDetailsData() {
  return useContext(AdditionalDetailsDataContext);
}
